import dayjs from 'dayjs';
import {
  CoverageList,
  FordProtect,
} from '../core/models/ford-user-with-vehicles-models/fordProtect.model';
import { SelectItem } from '../core/models/response/select-item.model';
import { ScheduleServices } from '../core/models/schedule/schedule-services.model';
import { ServiceStorage } from '../core/service/storage-service/service-storage.service';
import {
  FORD_PROTECT_COVERAGE_STATUS_ACTIVE,
  FORD_PROTECT_EXTEND_WARRANTY,
  FORD_PROTECT_EXTEND_WARRANTY_COVERAGE,
  FORD_PROTECT_MAINTENANCE_COVERAGE,
  FORD_PROTECT_STATUS_ACTIVE,
  sizes_screen,
} from './constants';
import { Source } from '@agenda-customer-ui/shared';

export const validateDateRegex = (data: string): boolean => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  return regex.test(data);
};

export const formattedDateDefault = (date: string): string => {
  const newDate = new Date(date);
  if (isNaN(newDate.getTime())) {
    return '';
  }
  const year = newDate.getFullYear();
  const month = (newDate.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = newDate.getUTCDate().toString().padStart(2, '0');
  return `${day}/${month}/${year}`;
};

export const timeIsOver = (clear: boolean) => {
  if (clear) {
    localStorage.clear();
    return true;
  }

  if ('@Ford-last-login-time' in localStorage) {
    const fiveMinutes = 300000;
    const localLastDate = localStorage.getItem(
      '@Ford-last-login-time'
    ) as string;
    const lastTime = new Date(parseInt(localLastDate, 10)).getTime();
    const currentTime = new Date().getTime();
    const diff = currentTime - lastTime;

    if (diff > fiveMinutes) {
      localStorage.clear();
      return true;
    }

    return false;
  } else {
    return true;
  }
};

export const getIsoDate = (date: string) => {
  const dateSplit = date.split('-');
  return `${dateSplit[0]}-${toTwoCharactersDate(
    dateSplit[1]
  )}-${toTwoCharactersDate(dateSplit[2])}`;
};

function toTwoCharactersDate(number: string) {
  return number.length === 1 ? `0${number}` : number;
}

// export const getCountries: SelectItem[] = [
//   {
//     title: 'ARGENTINA',
//     value: Country.ARGENTINA,
//     label: 'common.country.argentina',
//   },
//   {
//     title: 'BRAZIL',
//     value: Country.BRAZIL,
//     label: 'common.country.brazil',
//   },
//   { title: 'CHILE', value: Country.CHILE, label: 'common.country.chile' },
//   {
//     title: 'COLOMBIA',
//     value: Country.COLOMBIA,
//     label: 'common.country.colombia',
//   },
//   {
//     title: 'EQUADOR',
//     value: Country.EQUADOR,
//     label: 'common.country.equador',
//   },
//   {
//     title: 'VENEZUELA',
//     value: Country.VENEZUELA,
//     label: 'common.country.venezuela',
//   },
//   {
//     title: 'PERU',
//     value: Country.PERU,
//     label: 'common.country.peru',
//   },
// ];

export const formatTime = (time: number) => {
  if (time > -1) {
    const timePadded = String(time).padStart(4, '0');
    return timePadded.substring(0, 2) + ':' + timePadded.substring(2, 4);
  }
  return '';
};

export const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

export const formatDateByMask = (date: Date, mask: string): string => {
  return dayjs(date).format(mask);
};

export const isMobileScreen = () => {
  return window.innerWidth <= sizes_screen.MOBILE;
};


export const DEALER_DELIVERY = 1;

//#region FORD PROTECT

export const isFordProtectValid = (
  fordProtectData: FordProtect,
  serviceMonths: any
) => {
  if (
    fordProtectData &&
    fordProtectData.contractList &&
    fordProtectData.contractList.length > 0
  ) {
    const forpassActiveContracts = fordProtectData.contractList.filter(
      (c) =>
        c.productStatus.toLocaleLowerCase() === FORD_PROTECT_STATUS_ACTIVE &&
        c.productCode !== FORD_PROTECT_EXTEND_WARRANTY
    );
    const forpassActiveContractsFiltered = forpassActiveContracts.filter(
      (c) =>
        c.coverageList.filter((x) =>
          isServiceMonthInFordProtectCoverage(serviceMonths, x)
        ).length > 0
    );
    return forpassActiveContractsFiltered.length > 0;
  }
  return false;
};

// Determines if the vehicle has a valid Ford Protect "other plans" (extended
// warranty) contract, per business rule: active contract status, contract
// still within its validity period, and at least one active coverage whose
// coverageCode is an extended-warranty coverage (not maintenance/wear-part).
export const isFordProtectExtendedWarrantyValid = (
  fordProtectData: FordProtect
): boolean => {
  if (
    !fordProtectData ||
    !fordProtectData.contractList ||
    fordProtectData.contractList.length === 0
  ) {
    return false;
  }

  const today = dayjs().startOf('day');

  return fordProtectData.contractList.some((contract) => {
    const isActiveStatus =
      contract.productStatus?.toLocaleLowerCase() === FORD_PROTECT_STATUS_ACTIVE;

    const startDate = dayjs(contract.contractStartDate);
    const endDate = dayjs(contract.contractEndDate);
    const hasValidPeriod =
      startDate.isValid() &&
      endDate.isValid() &&
      !startDate.isAfter(today) &&
      !endDate.isBefore(today);

    const hasActiveCoverage =
      contract.coverageList?.some(
        (coverage) =>
          coverage.coverageStatus?.toLocaleLowerCase() ===
            FORD_PROTECT_COVERAGE_STATUS_ACTIVE &&
          FORD_PROTECT_EXTEND_WARRANTY_COVERAGE.includes(coverage.coverageCode)
      ) ?? false;

    return isActiveStatus && hasValidPeriod && hasActiveCoverage;
  });
};

export const isServiceMonthInFordProtectCoverage = (
  servicesMonth: any,
  fordProtectCoverage: CoverageList
): boolean => {
  const maintenanceCoverage = FORD_PROTECT_MAINTENANCE_COVERAGE.find(
    (x) => x === fordProtectCoverage.coverageCode
  );

  if (!maintenanceCoverage) {
    return false;
  }

  let finalCoverageVisits = fordProtectCoverage.coverageVisits;

  if (
    fordProtectCoverage.coverageStartVisit &&
    fordProtectCoverage.coverageStartVisit > 0
  ) {
    finalCoverageVisits =
      finalCoverageVisits + fordProtectCoverage.coverageStartVisit - 1;
    return (
      servicesMonth &&
      servicesMonth >= fordProtectCoverage.coverageStartVisit * 12 &&
      servicesMonth <= finalCoverageVisits * 12 &&
      fordProtectCoverage.coverageVisits !==
        fordProtectCoverage.coverageVisitsDone &&
      fordProtectCoverage.coverageStatus.toLocaleLowerCase() ===
        FORD_PROTECT_COVERAGE_STATUS_ACTIVE
    );
  }

  return (
    servicesMonth &&
    servicesMonth <= finalCoverageVisits * 12 &&
    fordProtectCoverage.coverageVisits !==
      fordProtectCoverage.coverageVisitsDone &&
    fordProtectCoverage.coverageStatus.toLocaleLowerCase() ===
      FORD_PROTECT_COVERAGE_STATUS_ACTIVE
  );
};

export const changePriceWithVehicleModel = (storage: ServiceStorage | ScheduleServices[]): void => {
  const vehicle = JSON.parse(localStorage.getItem('@Ford-vehicleModel') as string)
  const services = (storage instanceof ServiceStorage) ? storage.services.offeredServices : storage;

  services.forEach(service => {
    if (service.servicesByModelGroup.length >= 1) {
      for (let i = 0; i < service.servicesByModelGroup.length; i++) {
        const serviceByModelGroup = service.servicesByModelGroup[i]
        if (serviceByModelGroup.model.id === vehicle.modelGroup) {
          service.promotionalActive = serviceByModelGroup.promotionalActive;
          service.promotionalDescription = serviceByModelGroup.promotionalDescription;
          service.promotionalEndDate = serviceByModelGroup.promotionalEndDate;
          service.promotionalEndHour = serviceByModelGroup.promotionalEndHour;
          service.promotionalStartDate = serviceByModelGroup.promotionalStartDate;
          service.promotionalStartHour = serviceByModelGroup.promotionalStartHour;
          service.promotionalPrice = serviceByModelGroup.promotionalPrice;
          service.servicePrice = serviceByModelGroup.servicePrice;
        }
      }
    }
  });
}

//#endregion FORD PROTECT

export const isWebAccess = (source = ''): boolean => {
  const scheduleSource = source || sessionStorage.getItem('scheduleSource');

  if (!scheduleSource) return true;

  if (
    scheduleSource !== Source.perks &&
    scheduleSource !== Source.fordpass
  )
    return true;

  return false;
};
